/**
 * Animal Alphabet Grid - Bilingual interactive A-Z with emojis and voice
 * Click each letter to hear "A is for Alligator" (or Spanish equivalent)
 * Recreates animal_alphabet.html / aprender_letras with Wonderpath design
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Pencil } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { animalAlphabetData } from '../../data/animalAlphabetGrid';
import {
  speak as speakNatural,
  stop as stopNatural,
  speakLetterAnimalAndNature,
  playClickFeedback,
  prefetchCombinedUtterances,
} from '../../lib/naturalVoiceService';
import { VoiceDrawingCanvas } from './VoiceDrawingCanvas';

// Pastel inner-tile palette — cycles through the cards. Matches the
// reference: light blue, peach, mint, pink, lavender, mint-green.
const PASTEL_COLORS = [
  '#dbeef5',
  '#fce8d2',
  '#dcecda',
  '#fadcdc',
  '#e8defc',
  '#d6f0e0',
];

// Brand greens shared with the homepage / GlobalNav. Used for the letter
// glyph, italic animal name, and the primary CTA fill.
const INK_GREEN = '#2a6d5b';
const BRAND_GREEN = '#42a57a';
const SERIF = "'Fraunces', Georgia, serif";
const SANS = "'Inter', system-ui, -apple-system, sans-serif";

interface AnimalAlphabetGridProps {
  onTraceLetters?: () => void;
  onGoCounting?: () => void;
  onGoNames?: () => void;
}

export function AnimalAlphabetGrid({
  onTraceLetters,
  onGoCounting,
  onGoNames,
}: AnimalAlphabetGridProps) {
  const { language } = useLanguage();
  const [celebratingIndex, setCelebratingIndex] = useState<number | null>(null);
  const [traceLetter, setTraceLetter] = useState<{ letter: string; animal: string; emoji: string; funFact: string } | null>(null);
  const [traceLetterIndex, setTraceLetterIndex] = useState(0);
  const [traceLetterOrder, setTraceLetterOrder] = useState<'alphabetical' | 'random'>('alphabetical');
  const [lettersRevealed, setLettersRevealed] = useState(false);

  const content = {
    en: {
      title: 'Animal Alphabet',
      subtitle: 'Click each animal to hear it roar!',
      welcome: 'Welcome to the Animal Alphabet!',
      welcomeDesc: 'Every letter has a special animal friend! Click any animal to hear its name. Let\'s go!',
      tapToHear: 'Tap to hear',
      traceThis: 'Trace',
      traceLetters: 'Trace Letters',
      counting: 'Counting',
      names: 'Names',
      youDidIt: 'You Did It!',
      youDidItDesc: 'You just met 26 amazing animal friends! Can you remember them all? Try saying the alphabet with their names: A is for Alligator, B is for Bear, C is for Cat... Keep going!',
    },
    es: {
      title: 'ABC de Animales',
      subtitle: '¡Haz clic en cada animal para escucharlo!',
      welcome: '¡Bienvenido al Alfabeto de Animales!',
      welcomeDesc: '¡Cada letra tiene un amigo animal especial! Haz clic en cualquier animal para escuchar su nombre. ¡Vamos!',
      tapToHear: 'Toca para escuchar',
      traceThis: 'Trazar',
      traceLetters: 'Trazar Letras',
      counting: 'Contar',
      names: 'Nombres',
      youDidIt: '¡Lo Lograste!',
      youDidItDesc: '¡Acabas de conocer a 26 increíbles amigos animales! ¿Puedes recordarlos todos? Intenta decir el alfabeto con sus nombres: A de Caimán, B de Oso, C de Gato... ¡Sigue así!',
    },
  };

  const t = content[language];
  const isEn = language === 'en';

  useEffect(() => {
    if (!traceLetter) {
      setLettersRevealed(false);
      playClickFeedback(); // Instant audio so voice doesn't feel slow to start
      const lang = language as 'en' | 'es';
      const entries = animalAlphabetData.map((item) => ({
        letter: item.letter,
        animal: language === 'en' ? item.animalEn : item.animalEs,
        natureFact: language === 'en' ? item.funFactEn : item.funFactEs,
      }));
      // Warm all 26 clips immediately (parallel, bounded) — do not wait for welcome to finish
      void prefetchCombinedUtterances(entries, lang, 6);

      const prompt = `${t.welcome} ${t.welcomeDesc}`;
      speakNatural({
        text: prompt,
        language: lang,
        onComplete: () => setLettersRevealed(true),
      });
    }
    // Cancel any in-flight welcome on unmount or language change.
    // Without this, React StrictMode's double-mount in dev (and any
    // fast remount) can leave two TTS audios overlapping.
    return () => {
      stopNatural();
    };
  }, [language]);

  const speakAnimal = (item: (typeof animalAlphabetData)[0], index: number) => {
    setCelebratingIndex(index);
    setTimeout(() => setCelebratingIndex(null), 600);

    playClickFeedback();
    speakLetterAnimalAndNature(
      item.letter,
      isEn ? item.animalEn : item.animalEs,
      isEn ? item.funFactEn : item.funFactEs,
      language as 'en' | 'es'
    );
  };

  const openTrace = (item: (typeof animalAlphabetData)[0]) => {
    const idx = animalAlphabetData.findIndex((d) => d.letter === item.letter);
    setTraceLetterIndex(idx >= 0 ? idx : 0);
    setTraceLetter({
      letter: item.letter,
      animal: isEn ? item.animalEn : item.animalEs,
      emoji: isEn ? item.emojiEn : item.emojiEs,
      funFact: isEn ? item.funFactEn : item.funFactEs,
    });
  };

  const getNextTraceLetter = () => {
    if (traceLetterOrder === 'alphabetical') {
      const nextIdx = (traceLetterIndex + 1) % animalAlphabetData.length;
      setTraceLetterIndex(nextIdx);
      const item = animalAlphabetData[nextIdx];
      setTraceLetter({
        letter: item.letter,
        animal: isEn ? item.animalEn : item.animalEs,
        emoji: isEn ? item.emojiEn : item.emojiEs,
        funFact: isEn ? item.funFactEn : item.funFactEs,
      });
    } else {
      const nextIdx = Math.floor(Math.random() * animalAlphabetData.length);
      setTraceLetterIndex(nextIdx);
      const item = animalAlphabetData[nextIdx];
      setTraceLetter({
        letter: item.letter,
        animal: isEn ? item.animalEn : item.animalEs,
        emoji: isEn ? item.emojiEn : item.emojiEs,
        funFact: isEn ? item.funFactEn : item.funFactEs,
      });
    }
  };

  const handleTraceOrderChange = (order: 'alphabetical' | 'random') => {
    setTraceLetterOrder(order);
    if (order === 'random') {
      const nextIdx = Math.floor(Math.random() * animalAlphabetData.length);
      const item = animalAlphabetData[nextIdx];
      setTraceLetterIndex(nextIdx);
      setTraceLetter({
        letter: item.letter,
        animal: isEn ? item.animalEn : item.animalEs,
        emoji: isEn ? item.emojiEn : item.emojiEs,
        funFact: isEn ? item.funFactEn : item.funFactEs,
      });
    } else {
      setTraceLetterIndex(0);
      const item = animalAlphabetData[0];
      setTraceLetter({
        letter: item.letter,
        animal: isEn ? item.animalEn : item.animalEs,
        emoji: isEn ? item.emojiEn : item.emojiEs,
        funFact: isEn ? item.funFactEn : item.funFactEs,
      });
    }
  };

  // If tracing a specific letter, show VoiceDrawingCanvas
  if (traceLetter) {
    return (
      <VoiceDrawingCanvas
        letter={traceLetter.letter}
        animal={traceLetter.animal}
        emoji={traceLetter.emoji}
        funFact={traceLetter.funFact}
        onBack={() => setTraceLetter(null)}
        onNext={getNextTraceLetter}
        language={language as 'en' | 'es'}
        letterOrder={traceLetterOrder}
        onLetterOrderChange={handleTraceOrderChange}
      />
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ background: '#fbfbe8' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header — Fraunces serif title + Inter subtitle, matches the
            homepage's brand typography. Deep green `#2a6d5b` reads cleanly
            on the cream background. */}
        <div className="text-center mb-6 md:mb-8">
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-6xl md:text-8xl lg:text-9xl mb-2"
            style={{
              fontFamily: SERIF,
              fontWeight: 300,
              color: INK_GREEN,
              letterSpacing: '-0.015em',
            }}
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-3xl md:text-5xl"
            style={{
              fontFamily: SANS,
              fontWeight: 400,
              color: '#355c46',
            }}
          >
            {t.subtitle}
          </motion.p>
        </div>

        {/* Controls — primary Trace Letters in brand green; secondary
            outline pills route to the sibling games (Counting / Names)
            so users can hop between sections without going back. */}
        <div className="flex flex-wrap gap-3 justify-center mb-6">
          {onTraceLetters && (
            <motion.button
              onClick={onTraceLetters}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 text-white text-xl font-semibold py-3 px-6 rounded-full shadow-md"
              style={{ background: BRAND_GREEN, fontFamily: SANS }}
            >
              <Pencil className="w-5 h-5" />
              {t.traceLetters}
            </motion.button>
          )}
          {onGoCounting && (
            <motion.button
              onClick={onGoCounting}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              className="text-xl font-semibold py-3 px-6 rounded-full"
              style={{
                background: 'transparent',
                color: BRAND_GREEN,
                border: `1.5px solid ${BRAND_GREEN}`,
                fontFamily: SANS,
              }}
            >
              {t.counting}
            </motion.button>
          )}
          {onGoNames && (
            <motion.button
              onClick={onGoNames}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              className="text-xl font-semibold py-3 px-6 rounded-full"
              style={{
                background: 'transparent',
                color: BRAND_GREEN,
                border: `1.5px solid ${BRAND_GREEN}`,
                fontFamily: SANS,
              }}
            >
              {t.names}
            </motion.button>
          )}
        </div>

        {/* Alphabet grid - revealed after voice-over prompt - large cards for kids */}
        <motion.div
          initial={false}
          animate={{ opacity: lettersRevealed ? 1 : 0, pointerEvents: lettersRevealed ? 'auto' : 'none' }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-6 lg:gap-8 mb-8"
        >
          {animalAlphabetData.map((item, index) => {
            const pastel = PASTEL_COLORS[index % PASTEL_COLORS.length];
            const isCelebrating = celebratingIndex === index;

            return (
              <motion.div
                key={item.letter}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={lettersRevealed ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }}
                transition={{ delay: lettersRevealed ? index * 0.03 : 0, duration: 0.35 }}
                className={`group cursor-pointer ${isCelebrating ? 'animate-wiggle' : ''}`}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e0c8',
                  borderRadius: 18,
                  padding: 14,
                  transition: 'transform 200ms ease, box-shadow 200ms ease',
                }}
                whileHover={{ y: -2 }}
                onClick={() => speakAnimal(item, index)}
              >
                {/* Pastel inner tile holds the serif letter + the animated emoji */}
                <div
                  style={{
                    background: pastel,
                    borderRadius: 14,
                    padding: '36px 16px 20px',
                    textAlign: 'center',
                  }}
                >
                  <span
                    className="block"
                    style={{
                      fontFamily: SERIF,
                      fontSize: 'clamp(80px, 11vw, 132px)',
                      fontWeight: 600,
                      color: INK_GREEN,
                      lineHeight: 1,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {item.letter}
                  </span>
                  <span
                    className="block"
                    style={{
                      fontSize: 48,
                      lineHeight: 1,
                      marginTop: 14,
                      animation: 'float 3s ease-in-out infinite',
                    }}
                  >
                    {isEn ? item.emojiEn : item.emojiEs}
                  </span>
                </div>

                {/* Animal name — italic serif, centered, ink green */}
                <div
                  style={{
                    fontFamily: SERIF,
                    fontStyle: 'italic',
                    fontSize: 22,
                    fontWeight: 500,
                    color: INK_GREEN,
                    textAlign: 'center',
                    marginTop: 14,
                  }}
                >
                  {isEn ? item.animalEn : item.animalEs}
                </div>

                {/* "TAP TO HEAR" — small uppercase tracking */}
                <div
                  style={{
                    fontFamily: SANS,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: '#9a9a9a',
                    textAlign: 'center',
                    marginTop: 6,
                  }}
                >
                  {t.tapToHear}
                </div>

                {/* Per-card Trace CTA — kept; restyled in brand green */}
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    openTrace(item);
                  }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.96 }}
                  className="mt-3 w-full py-2.5 rounded-full flex items-center justify-center gap-2"
                  style={{
                    background: BRAND_GREEN,
                    color: '#ffffff',
                    fontFamily: SANS,
                    fontSize: 15,
                    fontWeight: 600,
                  }}
                >
                  <Pencil className="w-4 h-4" />
                  {t.traceThis}
                </motion.button>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Fun fact / celebration - revealed with letters */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: lettersRevealed ? 1 : 0 }}
          transition={{ delay: lettersRevealed ? 0.5 : 0, duration: 0.4 }}
          className="bg-gradient-to-r from-amber-200 to-amber-100 rounded-2xl p-6 text-center shadow-lg border-2 border-amber-300"
        >
          <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            🌟 {t.youDidIt} 🌟
          </h3>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {t.youDidItDesc} 🎉
          </p>
        </motion.div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
}
